import { Link, useNavigate, useParams } from 'react-router-dom';
import { useForm, useFormState } from "react-hook-form";
import { useEffect, useState } from "react";
import api from "../../../../api/Base";

// Types.
import NewRelicType from "../../../../types/integration/IntegrationType";
import Action from "../../../../types/Action";

// Constants.
import { ACTIONS, generalErrorMessage, newRelicAttributesArray } from '../../../../component/Constants';
import Alert from "../../../../component/Alert";

//Utils
import { findIntegrationValue, getAction, getValidClass } from "../../../../helpers/utils";

function Detail(props: { newRelic: NewRelicType[], refresh: Function, isAdmin: boolean }) {
    const { actionId } = useParams();
    const [action, setAction] = useState<Action>(ACTIONS.CREATE);
    const [apikeyElementType, setApiKeyElementType] = useState<string>('password');
    const [visiblePassword, setVisiblePassword] = useState<boolean>(false);
    const [hasChanged, setHasChanged] = useState(false);

    const navigate = useNavigate();
    
    // Redirect non-admin users
    useEffect(() => {
        if (!props.isAdmin) {
            navigate('/admin/integrations/new-relic/');
        }
    }, [props.isAdmin, navigate]);
    const isDelete = action.type === ACTIONS.DELETE.type;
    const {
        register,
        trigger,
        handleSubmit,
        getValues,
        setValue,
        control,
        formState: { errors }
    } = useForm<NewRelicType>({
        mode: "onBlur",
        reValidateMode: 'onSubmit',

        shouldFocusError: true
    });

    // Handle input change
    const handleChange = (event: any) => {
        const newValue = event.nativeEvent.data
        if (!hasChanged) {
            setHasChanged(true);
            setVisiblePassword(true);
            event.target.value = newValue?.trim();
            setValue('apikey', newValue, { shouldDirty: true });
        }
    };

    useEffect(() => {
        setVisiblePassword(action === ACTIONS.CREATE)
    }, [action]);

    useEffect(() => {
        getAction(actionId, setAction, setDisabled);
    }, [actionId]);

    const [disabled, setDisabled] = useState(isDelete);

    const parseEditResponse = (data: any) => {
        if (data.length) {
            // @ts-ignore
            newRelicAttributesArray.forEach(attr => setValue(attr, findIntegrationValue(attr, data, 'newrelic')))
            const dbmarurl = data.find((item: { name: string; }) => item?.name === 'newrelic.dbmarlin.url')
            setValue('dbmarurl', dbmarurl.value)
            void trigger()
        }
    }

    useEffect(() => {
        parseEditResponse(props.newRelic)
    }, [props.newRelic]);

    const { dirtyFields } = useFormState({
        control
    })


    const onSubmit = async (data: NewRelicType) => {
        setDisabled(true)
        const updatedValues: { name: string; value: any; }[] = []
        let result: any;
        let url = `/parameter`;

        Object.keys(data).forEach(item => {
            let name = (item === 'dbmarurl') ? `newrelic.dbmarlin.url` : `newrelic.${item}`
            // @ts-ignore
            const value = data[item] === true ? 'true' : data[item] === false ? 'false' : data[item]
            if (action.type === 'create' || action.type === 'delete') {
                updatedValues.push({ name, value })
                // @ts-ignore
            } else if (dirtyFields[item]) {
                updatedValues.push({ name, value })
            }
        })
        result = action.type === 'delete' ? await api.delete(url, { data: updatedValues }) : await api.post(url, updatedValues)

        if (result.status === 200) {
            navigate('/admin/integrations/new-relic')
            props.refresh()
        }
    }

    const isValid = Object.keys(errors).length === 0

    return (
        <div className="col">
            <div className="card">
                <div className="card-header">
                    <i className="fak fa-new-relic fa-fw" aria-hidden="true"/>
                    {action.title} New Relic
                </div>
                <div className="card-body">
                    <form onSubmit={handleSubmit(onSubmit)}>
                        {!isValid && (
                            <Alert message={generalErrorMessage.text} heading={generalErrorMessage.title}
                                   variant="alert-danger"/>
                        )}

                        <p>Please complete the required details below:</p>

                        <div className="row g-0 form">

                            {/* URL */}
                            <div className="col-xs-12 col-md-3">
                                <span className="required">
                                    <label htmlFor="url" className="base-url">URL</label>
                                </span>
                            </div>

                            <div className="col-xs-12 col-md-6 col-lg-4">
                                <input
                                    type="text"
                                    id="url"
                                    maxLength={250}
                                    {...register('url', {
                                        required : "Please fill in this field",
                                        pattern: {
                                            value: /^(ftp|http|https):\/\/[^ "]+$/,
                                            message: "Invalid Url"
                                        }
                                    })}
                                    className={`form-control ${getValidClass(errors.url, getValues('url'), isDelete)}`}
                                    defaultValue=''
                                    disabled={disabled}
                                    title={errors.url?.message}
                                    data-lpignore={true}
                                />
                                <small className="text-danger">{errors.url?.message}</small>

                            </div>
                            <div className="w-100"/>

                            {/* Account */}
                            <div className="col-xs-12 col-md-3">
                                <span className="required">
                                    <label htmlFor="url" className="base-url">Account</label>
                                </span>
                            </div>

                            <div className="col-xs-12 col-md-6 col-lg-4">
                                <input
                                    type="text"
                                    id="account"
                                    maxLength={250}
                                    {...register("account", { required: "Please fill in this field" })}
                                    className={`form-control ${getValidClass(errors.account, getValues('account'), isDelete)}`}
                                    defaultValue=''
                                    disabled={disabled}
                                    title={errors.account?.message}
                                    data-lpignore={true}
                                />
                                <small className="text-danger">{errors.account?.message}</small>
                            </div>
                            <div className="w-100"/>

                            {/* API Key */}
                            <div className="col-xs-12 col-md-3">
                                <span className="required">
                                    <label htmlFor="url" className="base-url">API Key</label>
                                </span>
                                {visiblePassword && (
                                    <i className="fas fa-eye fa-fw fa-xs ms-1" role="button" onClick={() => ((apikeyElementType === 'text') ? setApiKeyElementType('password') : setApiKeyElementType('text'))}/>
                                )}
                            </div>

                            <div className="col-xs-12 col-md-6 col-lg-4">
                                <input
                                    type={apikeyElementType}
                                    id="apikey"
                                    maxLength={250}
                                    {...register("apikey", { required: "Please fill in this field" })}
                                    className={`form-control ${getValidClass(errors.apikey, getValues('apikey'), isDelete)}`}
                                    defaultValue=''
                                    disabled={disabled}
                                    title={errors.apikey?.message}
                                    data-lpignore={true}
                                    onChange={handleChange}
                                />
                                <small className="text-danger">{errors.apikey?.message}</small>
                            </div>
                            <div className="w-100"/>

                            {/* DBmarlin URL */}
                            <div className="col-xs-12 col-md-3">
                                <span className="required">
                                    <label htmlFor="url" className="base-url">DBmarlin URL</label>
                                </span>
                            </div>

                            <div className="col-xs-12 col-md-6 col-lg-4">
                                <input
                                    type="text"
                                    id="dbmarurl"
                                    maxLength={250}
                                    {...register('dbmarurl', {
                                        required : "Please fill in this field",
                                        pattern: {
                                            value: /^(ftp|http|https):\/\/[^ "]+$/,
                                            message: "Invalid Url"
                                        }
                                    })}
                                    className={`form-control ${getValidClass(errors.dbmarurl, getValues('apikey'), isDelete)}`}
                                    defaultValue=''
                                    disabled={disabled}
                                    title={errors.dbmarurl?.message}
                                    data-lpignore={true}
                                />
                                <small className="text-danger">{errors.dbmarurl?.message}</small>
                            </div>
                            <div className="w-100"/>

                            {/* Enabled */}
                            <div className="col-xs-12 col-md-3">
                                <label className="auth">Enabled</label>
                            </div>
                            <div className="col-xs-12 col-md-6 col-lg-3">
                                <div className='form-check form-switch'>
                                    <input
                                        type="checkbox"
                                        id="enable"
                                        {...register("enable")}
                                        disabled={disabled}
                                        className="form-check-input"
                                        // onChange={() => setValue('enable', !getValues('enable'))}
                                        defaultChecked={true}
                                        data-lpignore={true}
                                    />
                                </div>
                            </div>
                            <div className="w-100"/>


                            {/* Actions */}
                            <div className="col-12 actions">
                                <Link to="/admin/integrations/new-relic" role="button" className="btn btn-secondary"><i
                                    className="fal fa-times-circle fa-fw fa-sm"/><span>Cancel</span></Link>
                                <button type="submit" className={`btn btn-${action.variant}`}><i
                                    className={`${action.icon} fa-sm`}/><span>{action.title}</span></button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    )
}

export default Detail;